import { WorkforceClinicSchema } from "@emr/contracts";
import { Fragment, type ReactNode, useCallback, useEffect, useState } from "react";
import { setWorkforceSiteId } from "../api/workforceRequestScope";
import type { ClinicalRuntimeConfig } from "../runtime/clinicalRuntime";
import { ClinicalRuntimeContext } from "./ClinicalRuntimeProvider";
import type { WorkforceSupabaseSession } from "./supabaseClient";

/** Resolve live delegations before any patient providers or query caches mount. */
export function WorkforceClinicBoundary({
  config,
  workforce,
  userId,
  children,
}: {
  config: ClinicalRuntimeConfig;
  workforce: WorkforceSupabaseSession;
  userId: string;
  children: ReactNode;
}) {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  // Re-discovery leaves the current clinic mounted. Clear its request scope
  // only when this authenticated boundary unmounts, not on effect re-runs.
  useEffect(() => () => setWorkforceSiteId(undefined), []);
  useEffect(() => {
    void retry;
    let active = true;
    void (async () => {
      await workforce.accessToken();
      const result = await workforce.client.schema("api").rpc("list_workforce_clinics");
      if (result.error) throw result.error;
      const clinics = WorkforceClinicSchema.array().parse(result.data);
      const selected = clinics.find((clinic) => clinic.siteId === config.siteId) ?? clinics[0];
      if (!selected) throw new Error("No active clinic access.");
      if (active) {
        setWorkforceSiteId(selected.siteId);
        setSiteId(selected.siteId);
        setError("");
      }
    })().catch(() => {
      if (active)
        setError(
          "Your clinic access could not be loaded. Retry or ask your administrator to check your access.",
        );
    });
    return () => {
      active = false;
    };
  }, [config.siteId, workforce, retry]);
  const switchSite = useCallback(
    async (nextSite: string, beforeChange?: () => Promise<void>) => {
      await workforce.accessToken();
      const result = await workforce.client
        .schema("api")
        .rpc("get_workforce_context", { p_site_id: nextSite });
      if (
        result.error ||
        result.data?.siteId !== nextSite ||
        result.data?.actor?.actorId !== userId
      )
        throw new Error("You do not have active access to that clinic.");
      await beforeChange?.();
      setWorkforceSiteId(nextSite);
      setSiteId(nextSite);
    },
    [workforce, userId],
  );
  if (!siteId)
    return (
      <main className="workforce-gate">
        <section>
          <h1>Your clinics</h1>
          {error ? (
            <>
              <p role="alert">{error}</p>
              <button
                type="button"
                onClick={() => {
                  setError("");
                  setRetry((value) => value + 1);
                }}
              >
                Retry clinic access
              </button>
              <button type="button" onClick={() => void workforce.clear()}>
                Sign out
              </button>
            </>
          ) : (
            <p role="status">Loading your clinic access…</p>
          )}
        </section>
      </main>
    );
  return (
    <ClinicalRuntimeContext.Provider
      value={{ mode: "supabase", config: { ...config, siteId }, workforce, switchSite }}
    >
      <Fragment key={`${userId}:${siteId}`}>{children}</Fragment>
    </ClinicalRuntimeContext.Provider>
  );
}
